import {zodResolver} from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputLabel,
  Stack,
  Typography,
} from '@mui/material';
import {ActionParams, HttpError, useSelect} from '@refinedev/core';
import {useForm} from '@refinedev/react-hook-form';
import Markdown from 'marked-react';
import React, {FC, useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router';
import {z as zod} from 'zod';

import {useGetFixedIndicatorsQuery} from '@app/store/api/Nemesis';
import {Create} from '@components/crud/create';
import {Edit} from '@components/crud/edit';
import {TissotField} from '@components/form/hook-form';
import {Field, Form} from '@minimal/components/hook-form';

import {useIndicatorChanges} from '../hooks/use-indicator-changes';
import {IndicatorSchema} from '../indicator-types';
import {IndicatorParameterForm} from './indicator-parameter-form';
import {ConfirmChangesModal} from './modal/confirm-changes-modal';

export type CreateIndicatorSchemaType = zod.infer<typeof IndicatorSchema>;

const ThematiqueOptions = [
  {value: 'Effectifs', label: 'Effectifs'},
  {value: 'Mouvements', label: 'Mouvements'},
  {value: 'Gestion Temps', label: 'Gestion Temps'},
  {value: 'Santé Sécurité', label: 'Santé Sécurité'},
  {value: 'Rémunération', label: 'Rémunération'},
];

export const IndicatorEditForm: FC<ActionParams> = ({action}) => {
  const {t} = useTranslation();
  const isCreation = action === 'create';
  const {data: fixedIndicators} = useGetFixedIndicatorsQuery();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState<CreateIndicatorSchemaType | null>(null);
  const {
    changes,
    showConfirmModal,
    showNoChangesModal,
    handleChangesDetection,
    setShowConfirmModal,
    setShowNoChangesModal,
  } = useIndicatorChanges(fixedIndicators || []);

  const defaultValues: CreateIndicatorSchemaType = {
    label: '',
    active: false,
    description: '',
    defaultDashboard: 'Effectifs',
    aide: '',
    fixedIndicatorId: 0,
    config: {
      x: '',
      y: '',
      z: '',
      totalX: false,
      totalY: false,
      totalZ: false,
      cutTables: false,
      donnee: '',
      methode: 'sum',
      filters: {present_end_of_month: ['Oui'], last_contract: ['Oui']},
      chartType: 'Table',
      possibleX: [],
      possibleDonnees: [],
      possibleChartTypes: [],
      possibleFilters: [],
    },
  };

  const methods = useForm<CreateIndicatorSchemaType, HttpError, CreateIndicatorSchemaType>({
    refineCoreProps: {
      action,
      successNotification: () => {
        return {
          message: t(`pages.pilotage-rh.indicators.notifications.${isCreation ? 'creation' : 'update'}.success`),
          type: 'success',
        };
      },
      errorNotification: () => {
        return {
          message: t(`pages.pilotage-rh.indicators.notifications.${isCreation ? 'creation' : 'update'}.error`),
          type: 'error',
        };
      },
    },
    mode: 'all',
    resolver: zodResolver(IndicatorSchema),
    defaultValues,
  });

  const {
    saveButtonProps,
    watch,
    refineCore: {query, mutation: error},
  } = methods;

  const isLoadingAccount = (query as any)?.isLoading;
  const data = (query as any)?.data;

  useEffect(() => {
    if (error && 'statusCode' in error && error.statusCode === 422) {
      const errorData = error as any;
      if (errorData.response?.data?.errors) {
        Object.entries(errorData.response.data.errors).forEach(([field, message]) => {
          methods.setError(field as keyof CreateIndicatorSchemaType, {
            type: 'manual',
            message: message as string,
          });
        });
      }
    }
  }, [error, methods]);

  const config = watch('config');
  const filters = config?.filters;

  useEffect(() => {
    if (Array.isArray(filters) && filters.length === 0) {
      methods.setValue('config.filters', {present_end_of_month: ['Oui']});
    }
  }, [filters, methods]);

  const {
    query: {isLoading: isLoadingOptions},
  } = useSelect({
    resource: 'workspaces',
    defaultValue: (data as any)?.data?.workspaces,
    optionLabel: 'label',
    optionValue: 'value',
  });

  const WrapperComponent = isCreation ? Create : Edit;
  const wrapperProps = !isCreation ? {canDelete: false} : {};

  useEffect(() => {
    if (!isCreation && (data as any)?.data && !initialFormValues && !methods.refineCore.formLoading && config) {
      const currentFormValues = methods.getValues();
      const deepCopy = JSON.parse(JSON.stringify(currentFormValues));

      setInitialFormValues(deepCopy);
    }
  }, [data, methods, isCreation, initialFormValues, methods.refineCore.formLoading, config]);

  const handleEditSubmit = useCallback(
    async (formValues: CreateIndicatorSchemaType) => {
      if (!initialFormValues) {
        methods.refineCore.onFinish?.(formValues);
        return;
      }
      handleChangesDetection(initialFormValues, formValues);
    },
    [initialFormValues, methods, handleChangesDetection],
  );

  const confirmAndSubmit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const formValues = methods.getValues();
      methods.refineCore.onFinish?.(formValues);
      setShowConfirmModal(false);
    } finally {
      setIsSubmitting(false);
    }
  }, [methods, setShowConfirmModal]);

  const modifiedSaveButtonProps = isCreation
    ? saveButtonProps
    : {
        ...saveButtonProps,
        onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault();
          methods.handleSubmit(handleEditSubmit)();
        },
      };

  return (
    <div style={{maxWidth: '600px'}}>
      <Form methods={methods}>
        <WrapperComponent
          saveButtonProps={modifiedSaveButtonProps}
          isLoading={(!isCreation && isLoadingAccount) || isLoadingOptions}
          headerButtons={
            <Button component={Link} to={`/pilotage-rh/indicators`} variant="outlined">
              {t('labels.back_to_multiples', {name: t('labels.indicators')})}
            </Button>
          }
          {...wrapperProps}
        >
          <Stack spacing={4}>
            <Card>
              <Stack divider={<Divider />} spacing={2}>
                <CardContent>
                  <Stack gap={2}>
                    <Field.Text
                      id="label"
                      name="label"
                      margin="none"
                      fullWidth
                      label={t('pages.pilotage-rh.indicators.fields.label')}
                    />
                  </Stack>
                </CardContent>
              </Stack>
            </Card>

            {config && (isCreation || !methods.refineCore.formLoading) && (
              <IndicatorParameterForm
                params={{...config, possibleFilters: config.possibleFilters || []}}
                setValue={methods.setValue}
                fixedIndicatorId={methods.watch('fixedIndicatorId') || 0}
              />
            )}
            <Card>
              <Stack divider={<Divider />} spacing={2}>
                <CardContent>
                  <Stack gap={2}>
                    <Typography variant="h6" gutterBottom>
                      {t('pages.pilotage-rh.indicators.form.help_and_description')}
                    </Typography>
                    <InputLabel id="parameter-donnee-label">
                      {t('pages.pilotage-rh.indicators.form.simple_description')}
                    </InputLabel>
                    <Field.Text multiline rows={4} id="description" name="description" />
                    <InputLabel>{t('pages.pilotage-rh.indicators.fields.aide')}</InputLabel>
                    <Box sx={{display: 'flex', flexDirection: 'row', gap: 2, mt: 2}}>
                      <Field.Text multiline minRows={4} id="aide" name="aide" sx={{width: '50%'}} />
                      <Box
                        sx={{
                          width: '50%',
                          border: '1px solid #ccc',
                          p: 2,
                          borderRadius: 1,
                          '& ul, & ol': {
                            padding: 'revert',
                            margin: 'revert',
                          },
                          '& li': {
                            listStyleType: 'disc',
                            padding: 'revert',
                            margin: 'revert',
                          },
                        }}
                      >
                        <Markdown value={methods.watch('aide') || ''} />
                      </Box>
                    </Box>
                  </Stack>
                </CardContent>
              </Stack>
            </Card>
            <Card>
              <Stack divider={<Divider />} spacing={2}>
                <CardContent>
                  <Stack gap={2}>
                    <TissotField.Select
                      id="defaultDashboard"
                      name="defaultDashboard"
                      label={t('pages.pilotage-rh.indicators.fields.defaultDashboard')}
                      options={ThematiqueOptions}
                    />
                    <Field.Checkbox id="active" name="active" label={t('pages.pilotage-rh.indicators.fields.active')} />
                  </Stack>
                </CardContent>
              </Stack>
            </Card>
          </Stack>
        </WrapperComponent>
      </Form>
      <div style={{display: 'flex', justifyContent: 'flex-end'}}></div>

      <ConfirmChangesModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmAndSubmit}
        isLoading={isSubmitting}
        changes={changes}
        title={t('pages.pilotage-rh.indicators.confirm_changes_title')}
      />

      <Dialog open={showNoChangesModal} onClose={() => setShowNoChangesModal(false)}>
        <DialogTitle sx={{textAlign: 'center', pt: 2}}>{t('pages.pilotage-rh.indicators.confirm_changes')}</DialogTitle>
        <DialogContent sx={{textAlign: 'center', pb: 2}}>
          <Typography sx={{mt: 1, color: 'text.secondary'}}>{t('pages.pilotage-rh.indicators.no_changes')}</Typography>
        </DialogContent>
        <DialogActions sx={{justifyContent: 'center', pb: 3}}>
          <Button onClick={() => setShowNoChangesModal(false)} variant="contained" color="primary">
            {t('buttons.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
